import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { isEmailUnverifiedMessage } from '@chompdome/shared/constants';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { ResendVerification } from '@/components/auth/resend-verification';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { googleFailureMessage } from '@/lib/oauth';
import { onIdentityChanged } from '@/lib/session-change';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/login')({
  component: LoginPage,
  /**
   * `reason` is how a failed Google sign-in gets here: the callback redirects to the app with a
   * code on the query string and `useGoogleAuthLanding` forwards it to this screen, because a
   * sign-in that did not work belongs on the sign-in page and not on a blank landing.
   */
  validateSearch: (search: Record<string, unknown>): { reason?: string } => ({
    reason: typeof search.reason === 'string' ? search.reason : undefined,
  }),
});

function LoginPage() {
  const navigate = useNavigate();
  const { reason } = Route.useSearch();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = trpc.auth.login.useMutation({
    onSuccess: async () => {
      // Everything cached a moment ago answered for whoever was signed in before this.
      await onIdentityChanged(queryClient);
      // THE LOBBY, NOT THE ARENA. Signing in is an errand — a player checking their stats, a
      // moderator on their way to the console, somebody who was signed out mid-session — and it
      // must never *by itself* start a match. Entering one is a press on the landing page, where
      // the room browser is, and that press is still one click away.
      navigate({ to: '/' });
    },
  });

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          {reason && (
            <p className="mb-4 text-sm text-destructive" role="alert">
              {googleFailureMessage(reason)}
            </p>
          )}

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              login.mutate({ email, password });
            }}
          >
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {login.error && (
              <p className="text-sm text-destructive" role="alert">
                {login.error.message}
              </p>
            )}

            {/* THE ONE FAILURE WITH AN AFFORDANCE ATTACHED. Everything else this form can be
                told is a sentence to read; an unconfirmed address is a sentence plus the one
                click that fixes it — otherwise a player whose confirmation mail never arrived
                reads a right password as a wrong one and has nowhere to go. Recognised by the
                shared constant both halves import, never by a substring. */}
            {isEmailUnverifiedMessage(login.error?.message) && <ResendVerification email={email} />}

            <Button type="submit" disabled={login.isPending} className="w-full">
              {login.isPending ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <GoogleSignInButton returnTo="/" />

          <p className="mt-4 text-center text-sm text-muted-foreground">
            No account?{' '}
            <Link to="/register" className="font-medium text-primary hover:underline">
              Sign up
            </Link>{' '}
            — or{' '}
            <Link to="/" className="font-medium text-primary hover:underline">
              play as a guest
            </Link>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
